/**
 * Background music (owner, 2026-11-03): a quiet loop under the sound effects, with its own
 * choice and volume in the menu. Only public-domain music is written here. The audio is
 * synthesised in code (`renderTrack`), so there is no file to download: a melody voice, a
 * plucked bass on each bar's first beat and soft chords on the other beats.
 *
 * A track: tempo, beats per bar and its bars. Each bar has the melody (note names as in
 * songs.ts, `:2` = two beats, default one; `r` = rest), the bass note and the chord.
 */
export interface Track {
	name: string;
	bpm: number;
	beatsPerBar: number;
	/** Played in order, then repeated; each pass may use another voice for the melody. */
	passes: readonly { voice: MelodyVoice; transpose: number }[];
	bars: readonly { melody: string; bass: string; chord: string }[];
}

export type MelodyVoice = "flute" | "xylophone";

export const MUSIC = {
	// Camille Saint-Saëns, Danse macabre, op. 40 (1874): the waltz theme, as in the score
	// (Mouvement modéré de Valse, G minor), an octave up. The skeletons' dance at midnight;
	// the second pass is on the xylophone, the bones' rattle in the original.
	dansemacabre: {
		name: "Dödsdansen",
		bpm: 168,
		beatsPerBar: 3,
		passes: [
			{ voice: "flute", transpose: 12 },
			{ voice: "xylophone", transpose: 12 },
		],
		bars: [
			{ melody: "G4:2 G4", bass: "G2", chord: "G3 Bb3 D4" },
			{ melody: "F4:2 F4", bass: "F2", chord: "F3 Bb3 D4" },
			{ melody: "E4:2 E4", bass: "E2", chord: "E3 G3 C4" },
			{ melody: "Eb4:2 Eb4", bass: "Eb2", chord: "Eb3 G3 C4" },
			{ melody: "D4 E4 G4", bass: "D2", chord: "D3 F#3 C4" },
			{ melody: "F#4 E4 D4", bass: "A1", chord: "D3 F#3 C4" },
			{ melody: "E4:2 E4", bass: "A2", chord: "C#3 E3 G3" },
			{ melody: "F#4:2 D4", bass: "D2", chord: "D3 F#3 A3" },
		],
	},
} as const satisfies Record<string, Track>;

export type MusicId = keyof typeof MUSIC;
export const MUSIC_IDS = Object.keys(MUSIC) as MusicId[];

/** Menu volume steps → the Phaser sound's volume (kept under the effects, which play at 0.7). */
export const MUSIC_VOLUMES = { low: 0.15, mid: 0.3, high: 0.55 } as const;
export type MusicVolume = keyof typeof MUSIC_VOLUMES;
export const MUSIC_VOLUME_IDS = Object.keys(MUSIC_VOLUMES) as MusicVolume[];

/** One note to synthesise: start and length in seconds, pitch as MIDI. */
export interface NoteEvent {
	at: number;
	length: number;
	midi: number;
	voice: MelodyVoice | "bass" | "chord";
}

const STEPS: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** "C#5", "Bb4", "A1" → MIDI; NaN if not a note. */
export function noteMidi(note: string): number {
	const match = /^([A-G])([#b]?)(-?\d)$/.exec(note);
	if (!match) return Number.NaN;
	const [, letter = "", accidental, octave = ""] = match;
	return (
		12 * (Number(octave) + 1) +
		(STEPS[letter] ?? 0) +
		(accidental === "#" ? 1 : accidental === "b" ? -1 : 0)
	);
}

/** A bar's melody as (note, beats) pairs; note = null for a rest. */
export function parseMelody(melody: string): { note: string | null; beats: number }[] {
	return melody
		.split(/\s+/)
		.filter(Boolean)
		.map((token) => {
			const [note = "", beats = "1"] = token.split(":");
			return { note: note === "r" ? null : note, beats: Number(beats) };
		});
}

/** One loop's length in seconds. */
export function loopSeconds(track: Track): number {
	return (track.passes.length * track.bars.length * track.beatsPerBar * 60) / track.bpm;
}

/** Every note of one loop, in time order per voice. */
export function trackNotes(track: Track): NoteEvent[] {
	const beat = 60 / track.bpm;
	const notes: NoteEvent[] = [];
	let bar = 0;
	for (const pass of track.passes) {
		for (const { melody, bass, chord } of track.bars) {
			const start = bar * track.beatsPerBar * beat;
			let t = start;
			for (const { note, beats } of parseMelody(melody)) {
				if (note) {
					notes.push({
						at: t,
						length: beats * beat,
						midi: noteMidi(note) + pass.transpose,
						voice: pass.voice,
					});
				}
				t += beats * beat;
			}
			notes.push({ at: start, length: beat, midi: noteMidi(bass), voice: "bass" });
			for (let b = 1; b < track.beatsPerBar; b++) {
				for (const n of chord.split(/\s+/)) {
					notes.push({ at: start + b * beat, length: beat, midi: noteMidi(n), voice: "chord" });
				}
			}
			bar++;
		}
	}
	return notes;
}

/** Partials (multiple of the pitch, level), decay time constant (s; 0 = held) and loudness. */
const VOICES: Record<
	NoteEvent["voice"],
	{ partials: [number, number][]; decay: number; gain: number }
> = {
	// Soft and round, held for the note's length, with a little vibrato.
	flute: {
		partials: [
			[1, 1],
			[2, 0.25],
			[3, 0.08],
		],
		decay: 0,
		gain: 0.32,
	},
	// Bright and dry: a wooden bar's strong third partial, dying away fast.
	xylophone: {
		partials: [
			[1, 1],
			[3, 0.4],
			[6.2, 0.1],
		],
		decay: 0.16,
		gain: 0.4,
	},
	// Pizzicato double bass.
	bass: {
		partials: [
			[1, 1],
			[2, 0.4],
			[3, 0.15],
		],
		decay: 0.28,
		gain: 0.4,
	},
	chord: {
		partials: [
			[1, 1],
			[2, 0.2],
		],
		decay: 0.12,
		gain: 0.09,
	},
};

const ATTACK_S = 0.012;
const RELEASE_S = 0.08;
/** Peak level of the rendered loop; the menu's volume scales it. */
export const MUSIC_PEAK = 0.9;

/** A sine table (one period) with linear interpolation: far cheaper than Math.sin per sample. */
const SINE_SIZE = 4096;
const SINE = Float32Array.from({ length: SINE_SIZE + 1 }, (_, i) =>
	Math.sin((2 * Math.PI * i) / SINE_SIZE),
);
/** sin(2π · x / SINE_SIZE). */
function sine(x: number): number {
	const p = x % SINE_SIZE;
	const i = Math.floor(p);
	const a = SINE[i] ?? 0;
	return a + ((SINE[i + 1] ?? 0) - a) * (p - i);
}

/**
 * Synthesises one loop as mono samples. Notes that ring past the end wrap round to the start,
 * so the loop repeats without a click.
 */
export function renderTrack(track: Track, sampleRate: number): Float32Array<ArrayBuffer> {
	const total = Math.round(loopSeconds(track) * sampleRate);
	const out = new Float32Array(total);
	for (const note of trackNotes(track)) {
		const v = VOICES[note.voice];
		const freq = 440 * 2 ** ((note.midi - 69) / 12);
		const ring = v.decay > 0 ? Math.min(note.length, v.decay * 5) : note.length;
		const length = ring + RELEASE_S;
		const first = Math.round(note.at * sampleRate);
		const count = Math.round(length * sampleRate);
		const vibrato = note.voice === "flute";
		const fall = v.decay > 0 ? Math.exp(-1 / (v.decay * sampleRate)) : 1;
		const step = (freq * SINE_SIZE) / sampleRate;
		const multiples = v.partials.map((p) => p[0]);
		const levels = v.partials.map((p) => p[1]);
		let decay = 1;
		let phase = 0;
		for (let i = 0; i < count; i++) {
			const t = i / sampleRate;
			let env = Math.min(1, t / ATTACK_S) * decay;
			decay *= fall;
			if (t > ring) env *= Math.max(0, 1 - (t - ring) / RELEASE_S);
			phase += vibrato && t > 0.15 ? step * (1 + 0.004 * sine(5 * t * SINE_SIZE)) : step;
			let s = 0;
			for (let k = 0; k < multiples.length; k++) {
				s += (levels[k] ?? 0) * sine(phase * (multiples[k] ?? 0));
			}
			const at = (first + i) % total;
			out[at] = (out[at] ?? 0) + s * env * v.gain;
		}
	}
	let peak = 0;
	for (const s of out) peak = Math.max(peak, Math.abs(s));
	if (peak > 0) for (let i = 0; i < total; i++) out[i] = ((out[i] ?? 0) * MUSIC_PEAK) / peak;
	return out;
}
